/* eslint-disable no-param-reassign */
import { useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import { TableColumn } from 'tea-component';
import { PROJECT_COLUMN_WIDTH } from '../utils';
import { CalendarCurrentBtnArrowType } from '../../../../CalendarPanel/enums';

import type { ICalendarTableDTO } from '@gp/types/calendar';

const getCurrentDateViewableRange = (params: {
  tableBody: HTMLElement;
  maxScrollLeft: number;
  currentDateLeft: number;
  currentDateWidth: number;
  offset: number;
}) => {
  const { tableBody, maxScrollLeft, currentDateLeft, currentDateWidth, offset } = params || {};
  if (Number.isNaN(maxScrollLeft)) return { min: Number.NaN, max: Number.NaN };
  if (!tableBody) return { min: Number.NaN, max: Number.NaN };
  return {
    min: Math.max(currentDateLeft - (tableBody.offsetWidth - offset) + currentDateWidth, 0),
    max: Math.min(currentDateLeft, maxScrollLeft),
  };
};

export default function useCurrentScroll(props: {
  table: React.RefObject<any>;
  isLoading?: boolean;
  isNoData?: boolean;
  dateType?: ScheduledateTypes;
  columns?: TableColumn<ICalendarTableDTO>[];
}) {
  const { table, isLoading = true, isNoData = false, dateType = 'month', columns } = props;

  const [scrollLeft, setScrollLeft] = useState(Number.NaN);
  const [isOverflowX, setIsOverflowX] = useState(false);
  const [currentDateViewableRange, setCurrentDateViewableRange] = useState<{
    min: number;
    max: number;
  }>({
    min: Number.NaN,
    max: Number.NaN,
  });

  const currentDate = useMemo<string>(
    () => moment().startOf(dateType).format('YYYY-MM-DD'),
    [dateType],
  );

  const isContainsCurrentDate = useMemo<boolean>(() => {
    if (!Array.isArray(columns) || !columns.length) return false;
    return !!columns.find((column) => column?.key === currentDate);
  }, [currentDate, columns]);

  const currentDateWidth = useMemo<number>(() => {
    if (isLoading || isNoData) return 0;
    if (!Array.isArray(columns) || !columns.length) return 0;
    if (!isContainsCurrentDate) return 0;
    const column = columns.find((column) => column?.key === currentDate);
    return (column?.width as number) || 0;
  }, [isLoading, isNoData, currentDate, isContainsCurrentDate, columns]);

  const currentDateLeft = useMemo<number>(() => {
    if (isLoading || isNoData) return 0;
    if (!Array.isArray(columns) || !columns.length) return 0;
    if (!isContainsCurrentDate) return 0;
    let left = 0;
    for (const column of columns.slice(1)) {
      const { key, width } = column || {};
      if (key === currentDate) {
        break;
      }
      left += width as number;
    }
    return left;
  }, [isLoading, isNoData, currentDate, isContainsCurrentDate, columns]);

  const getTableBody = () => table.current?.querySelector('.tea-table__body');

  const disabled = useMemo<boolean>(() => {
    if (isLoading || isNoData) return false;
    if (!Array.isArray(columns) || !columns.length) return false;
    return !isContainsCurrentDate;
  }, [isLoading, isNoData, isContainsCurrentDate, columns]);

  const visible = useMemo<boolean>(() => {
    if (isLoading || isNoData) return false;
    if (disabled) return true;
    if (!isOverflowX) return false;
    if (typeof scrollLeft !== 'number' || Number.isNaN(scrollLeft)) return false;
    const { min, max } = currentDateViewableRange || {};
    if (typeof min !== 'number' || Number.isNaN(min)) return false;
    if (typeof max !== 'number' || Number.isNaN(max)) return false;
    return scrollLeft < min || scrollLeft > max;
  }, [isLoading, isNoData, disabled, isOverflowX, scrollLeft, currentDateViewableRange]);

  const currentBtnType = useMemo<CalendarCurrentBtnArrowType>(() => {
    if (isLoading || isNoData) return CalendarCurrentBtnArrowType.Hidden;
    if (disabled) return CalendarCurrentBtnArrowType.Hidden;
    if (!isOverflowX) return CalendarCurrentBtnArrowType.Hidden;
    /** ********* 数据异常，隐藏currentBtn按钮 ********* **/
    if (typeof scrollLeft !== 'number' || Number.isNaN(scrollLeft))
      return CalendarCurrentBtnArrowType.Hidden;
    const { min, max } = currentDateViewableRange || {};
    if (typeof min !== 'number' || Number.isNaN(min)) return CalendarCurrentBtnArrowType.Hidden;
    if (typeof max !== 'number' || Number.isNaN(max)) return CalendarCurrentBtnArrowType.Hidden;
    /** ********* 数据异常，隐藏currentBtn按钮 ********* **/
    if (scrollLeft < min) return CalendarCurrentBtnArrowType.Right;
    if (scrollLeft > max) return CalendarCurrentBtnArrowType.Left;
    return CalendarCurrentBtnArrowType.Hidden;
  }, [isLoading, isNoData, disabled, isOverflowX, scrollLeft, currentDateViewableRange]);

  const handleScroll2Current = (tableBody = getTableBody()) => {
    if (!isOverflowX) return;
    if (tableBody) {
      tableBody.scrollLeft = currentDateLeft;
    }
  };

  const handleScroll = (e?: any) => {
    const scrollLeftTmp = e?.target?.scrollLeft;
    setScrollLeft(scrollLeftTmp);
  };

  useEffect(() => {
    if (isLoading || isNoData) return;
    const tableBody = getTableBody();
    handleScroll2Current(tableBody);

    if (tableBody) {
      const maxScrollLeftTmp = tableBody.scrollWidth - tableBody.offsetWidth;
      const currentDateViewableRangeTmp = getCurrentDateViewableRange({
        tableBody,
        maxScrollLeft: maxScrollLeftTmp,
        currentDateLeft,
        currentDateWidth,
        offset: PROJECT_COLUMN_WIDTH,
      });
      setIsOverflowX(tableBody.scrollWidth > tableBody.offsetWidth);
      setCurrentDateViewableRange(currentDateViewableRangeTmp);
      tableBody.addEventListener('scroll', handleScroll, false);
    }

    return () => {
      tableBody.removeEventListener('scroll', handleScroll, false);
    };
  }, [isLoading, isNoData, currentDateLeft, currentDateWidth]);

  return {
    currentDateLeft,
    currentDateWidth,
    disabled,
    visible,
    currentBtnType,
    onScroll2Current: handleScroll2Current,
  };
}
